import React, { useEffect, useRef } from 'react'
import { connect } from 'dva'
import Axios from 'axios'
import flatten from 'lodash-es/flatten'
import get from 'lodash-es/get'
import PropTypes from 'prop-types'
import Graphic from '@arcgis/core/Graphic'
import Point from '@arcgis/core/geometry/Point'

import { BASEMAPS, FEATURE_TYPES, MAP_LAYER_IDS, MAX_ZOOM_LEVELS, SIDEBAR_VIEWS, STATES_DATA } from '../../../lib/constants'
import { checkKeyWords, formatDate, setBoundingBox } from '../../../lib/utils'
import { getLayerMetadata } from '../../../lib/historicalImagery'
import { hasLayers, hasSubCategories } from '../../../lib/layers.js'
import { reverseGeocode, stateInfo, huntUnitQuery } from '../../../services/state'
import { getLocationPinSymbol, getLocationPinLoadingMarkup } from '../../../lib/svgCache'
import actions from './actions'
import ghAnalytics from '../../../lib/analytics'

/*
  #TODO: This doesn't have to be like this...
  Heavily refactor, preferrably remove with move to FeatureLayers.
  -- TJC - June 29, 2021
*/
const LOCATION_PIN_ID = 'gh-location-pin'

// Inject the spinner keyframe once per page load (safe to call multiple times).
const SPINNER_STYLE_ID = 'gh-location-spinner-kf'
const ensureSpinnerKeyframe = () => {
  if (document.getElementById(SPINNER_STYLE_ID)) return
  const el = document.createElement('style')
  el.id = SPINNER_STYLE_ID
  el.textContent = '@keyframes ghLocSpin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}'
  document.head.appendChild(el)
}

// Stored at module scope so they survive re-renders.
let locationPinGraphic = null  // settled pin graphic
let pinLocation = null          // { longitude, latitude } while spinner is shown


function ClickPopupHandler(props) {
  // Keep a ref to always-current props so the click listener (registered once)
  // never captures stale closures.
  const propsRef = useRef(props)
  useEffect(() => { propsRef.current = props })

  const getLocation = (attributes, geometry, point) => {
    switch (attributes.properties.ghType) {
      case FEATURE_TYPES.Point:
        return geometry
      case FEATURE_TYPES.Polyline:
      case FEATURE_TYPES.Polygon:
      case FEATURE_TYPES.Tracks:
        return { latitude: point.latitude, longitude: point.longitude }
      default:
        return { latitude: 0, longitude: 0 }
    }
  }

  // Re-register listeners whenever the view switches (2D ↔ 3D).
  useEffect(() => {
    const { map, view } = propsRef.current

    view.popup.autoOpenEnabled = false
    view.popup.dockEnabled = false
    view.popup.collapseEnabled = false
    view.popup.spinnerEnabled = true
    view.popup.dockOptions = { buttonEnabled: false }

    // ── DOM spinner (CSS-animated; works in both MapView and SceneView) ───────
    //
    // picture-marker symbol swaps are async WebGL texture uploads in 3D, so
    // JS per-frame animation never renders. A plain DOM element with a CSS
    // `rotate` keyframe is reliable in both 2D and 3D.

    ensureSpinnerKeyframe()

    const spinnerEl = document.createElement('div')
    Object.assign(spinnerEl.style, {
      position: 'absolute',
      width: '34px',
      height: '34px',
      marginLeft: '-17px',   // centre the 34px icon on the anchor point
      marginTop: '-17px',
      pointerEvents: 'none',
      zIndex: '10',
      animation: 'ghLocSpin 1s linear infinite',
      transformOrigin: 'center',
      display: 'none',
    })
    spinnerEl.innerHTML = getLocationPinLoadingMarkup()
    if (view.container) {
      view.container.appendChild(spinnerEl)
    }

    const positionSpinner = () => {
      if (!pinLocation || !view.ready) return
      try {
        const s = view.toScreen(new Point({ longitude: pinLocation.longitude, latitude: pinLocation.latitude }))
        spinnerEl.style.left = s ? `${s.x}px` : '-9999px'
        spinnerEl.style.top = s ? `${s.y}px` : '-9999px'
      } catch {
        spinnerEl.style.left = '-9999px'
        spinnerEl.style.top = '-9999px'
      }
    }

    const showSpinner = (longitude, latitude) => {
      pinLocation = { longitude, latitude }
      positionSpinner()
      spinnerEl.style.display = 'block'
    }

    const hideSpinner = () => {
      spinnerEl.style.display = 'none'
    }

    // ── pin helpers ───────────────────────────────────────────────────────────

    const removePin = () => {
      hideSpinner()
      pinLocation = null
      if (locationPinGraphic) {
        view.graphics.remove(locationPinGraphic)
        locationPinGraphic = null
      }
    }

    const placeLocationPin = (longitude, latitude) => {
      removePin()
      showSpinner(longitude, latitude)
    }

    const stopLocationPinSpinner = () => {
      const loc = pinLocation
      if (!loc) return   // guard against double calls
      hideSpinner()
      pinLocation = null
      if (locationPinGraphic) {
        view.graphics.remove(locationPinGraphic)
        locationPinGraphic = null
      }
      const graphic = new Graphic({
        geometry: new Point({ longitude: loc.longitude, latitude: loc.latitude }),
        symbol: getLocationPinSymbol(),
        attributes: { id: LOCATION_PIN_ID },
      })
      view.graphics.add(graphic)
      locationPinGraphic = graphic
    }

    const buildLayersRequest = async ({ layer, overlay, point }) => {
      const { zoomLevel } = propsRef.current
      const setDistanceRadius = multiplier => Math.ceil(Math.pow(1.2, (MAX_ZOOM_LEVELS - zoomLevel) * multiplier))
      let distanceRadius = 10

      if (layer) {
        if (zoomLevel >= 10) distanceRadius = setDistanceRadius(1.75)
      } else if (overlay) {
        distanceRadius = setDistanceRadius(3)
      }

      const config = {
        baseUrl: '',
        params: {
          distance: distanceRadius, // In meters, the radius of our point
          f: 'json',
          geometry: point.toString(),
          geometryType: 'esriGeometryPoint',
          inSR: 4326,
          outFields: '*',
          returnGeometry: 'false',
          spatialRel: 'esriSpatialRelIntersects',
          units: 'esriSRUnit_Meter'
        }
      }

      if (layer && layer.urls && layer.urls.mainRes) {
        const isRoadsOrTrails = checkKeyWords(layer.category, ['roads', 'trails'])
        if (isRoadsOrTrails) {
          const boundingBox = setBoundingBox(point, distanceRadius)
          config.params = {
            f: 'json',
            geometry: { ...boundingBox, spatialReference: { wkid: 102100 } },
            geometryType: 'esriGeometryEnvelope',
            outFields: '*',
            returnGeometry: 'false',
            spatialRel: 'esriSpatialRelIntersects',
            units: 'esriSRUnit_Meter'
          }
        }
        config.url = `${layer.urls.mainRes}/query`
      } else if (overlay) {
        config.url = `${overlay.url}/query`
      }

      if (config.url) return Axios(config)
    }

    const getSearchRequest = async point => {
      const { activeLayers, activeOverlays, dispatch, gotoLocationSidebar,
              historicalItem, isHistoricalView, isTopographicBasemap, isSidebarClosed, zoomLevel } = propsRef.current

      let selectedStateName = null
      let selectedStateCounty = null

      // Step 1: Resolve state name
      const stateInfoResult = await stateInfo(point, 1)
      if (stateInfoResult.success) {
        const { data: stateInfoData } = stateInfoResult.data
        if (stateInfoData.features && stateInfoData.features.length) {
          selectedStateCounty = stateInfoData.features[0].attributes.NAME
          selectedStateName = stateInfoData.features[0].attributes.STATE_NAME
        }
      }
      if (!selectedStateName) {
        const geocodeRequest = await reverseGeocode(`${point[0]}, ${point[1]}`)
        if (geocodeRequest.success) {
          const geocodeData = geocodeRequest.data.data
          if (geocodeData.address && geocodeData.address.PlaceName) {
            selectedStateName = geocodeData.address.PlaceName
          }
        }
      }

      // Historical imagery path
      if (isHistoricalView && !isTopographicBasemap) {
        stopLocationPinSpinner()
        let { center: { x, y }, spatialReference: { latestWkid, wkid }, zoom = 0 } = view
        const params = {
          geometry: { spatialReference: { latestWkid, wkid }, x, y },
          item: historicalItem,
          zoom: zoomLevel || zoom
        }
        const historicalMetadata = await getLayerMetadata(params)
        let popUpTemplate = { title: 'About this location' }
        let content = '<span style="font-size: 17px; color: #787878">Not available</span>'
        let tag = '<span style="font-size: 12px">Image Date:</span>'
        if (historicalMetadata?.captureDate) {
          const date = formatDate(historicalMetadata.captureDate, 'MMM D, YYYY')
          content = `<span style="font-size: 17px">${date}</span>`
        }
        popUpTemplate.title = `${tag}\n${content}`
        view.popup.alignment = 'auto'
        view.popup.open({
          ...popUpTemplate,
          location: { longitude: point[0], latitude: point[1] },
        })
        return
      }

      // Step 2: Open sidebar, then immediately set locationLoading=true.
      // gotoLocationSidebar calls setLayersAtLocation internally, so we must
      // set locationLoading AFTER it to avoid it being reset to false.
      dispatch({ type: 'layers/setHuntUnitsAtLocation', payload: [] })
      dispatch({ type: 'layers/setHuntUnitsLoading', payload: true })
      gotoLocationSidebar(point, isSidebarClosed, [], [], selectedStateCounty, selectedStateName)
      dispatch({ type: 'layers/setLocationLoading', payload: true })

      // Step 3: Hunt unit query — resolve independently so UI can show hunt units as soon as they arrive
      if (selectedStateName) {
        huntUnitQuery(selectedStateName, point)
          .then(res => {
            const huntUnitBody = get(res, ['data', 'data'], null)
            const huntUnitsAtLocation = huntUnitBody?.features?.length
              ? [{ layer: { state: selectedStateName }, metadata: huntUnitBody }]
              : []
            dispatch({ type: 'layers/setHuntUnitsAtLocation', payload: huntUnitsAtLocation })
            dispatch({ type: 'layers/setHuntUnitsLoading', payload: false })
          })
          .catch(err => {
            console.error(err)
            dispatch({ type: 'layers/setHuntUnitsAtLocation', payload: [] })
            dispatch({ type: 'layers/setHuntUnitsLoading', payload: false })
          })
      } else {
        dispatch({ type: 'layers/setHuntUnitsLoading', payload: false })
      }

      // Step 4: Active layer queries — only layers matching the clicked state
      const selectedLayers = selectedStateName
        ? activeLayers.filter(l => l.state.toLowerCase() === selectedStateName.toLowerCase())
        : [...activeLayers]
      const promises = [
        ...selectedLayers.map(layer => buildLayersRequest({ layer, point })),
        ...activeOverlays.map(overlay => buildLayersRequest({ overlay, point })),
      ]

      Promise.all(promises)
        .catch(err => { console.error(err); return [] })
        .then(layerResponses => {
          const layersAtLocation = []
          const overlaysAtLocation = []
          const responses = layerResponses || []
          responses.forEach((res, i) => {
            const features = get(res, ['data', 'features'], null)
            if (!features?.length) return
            if (i < selectedLayers.length) {
              layersAtLocation.push({ layer: selectedLayers[i], metadata: res.data })
            } else {
              overlaysAtLocation.push({ overlay: activeOverlays[i - selectedLayers.length], metadata: res.data })
            }
          })
          ghAnalytics.logLocationInfoViewed(point, layersAtLocation.length)
          stopLocationPinSpinner()
          dispatch({
            type: 'layers/setLayersAtLocation',
            payload: { county: selectedStateCounty, layersAtLocation, overlaysAtLocation, point, state: selectedStateName },
          })
          dispatch({ type: 'layers/setLocationLoading', payload: false })
        })
        .catch(err => {
          console.error('[ClickPopupHandler] Error loading location data:', err)
          stopLocationPinSpinner()
          dispatch({ type: 'layers/setLocationLoading', payload: false })
        })
    }

    // ── Event listeners ──────────────────────────────────────────────────────

    // Reposition the spinner whenever the camera / extent changes (2D pan or 3D rotate).
    const viewpointWatch = view.watch('viewpoint', positionSpinner)

    // Set CSS cursor directly on the view surface so it overrides ArcGIS's own
    // class-based cursor management in both MapView and SceneView. Using
    // `view.cursor` alone doesn't work in 3D because the SceneView's navigation
    // fires a cursor-reset handler after ours. Empty string removes the inline
    // override and hands control back to ArcGIS's stylesheets.
    const surface = view.container?.querySelector('.esri-view-surface')
    const resetCursor = () => { if (surface) surface.style.cursor = '' }
    const dragListener = view.on('drag', event => {
      if (propsRef.current.activeTool != null) return
      if (!surface) return
      const grabbing = event.action !== 'end' && event.action !== 'cancel'
      surface.style.cursor = grabbing ? 'grabbing' : ''
    })
    // Reset if the pointer leaves the map mid-drag so it doesn't stay stuck.
    surface?.addEventListener('pointerleave', resetCursor)

    const popupEventListener = view.popup.on('trigger-action', event => {
      if (event.action.id == 'feature-clicked') {
        const { features, selectFeature } = propsRef.current
        const featureIndex = features.findIndex(feat => feat.uuid == event.action.clickedUuid)
        if (featureIndex >= 0) {
          selectFeature(features[featureIndex])
          view.popup.close()
        }
      }
    })

    const clickListener = view.on('click', event => {
      const { activeTool, isUpdatingLocation } = propsRef.current
      if (isUpdatingLocation) return
      if (activeTool != null) return

      // Show spinner immediately — don't wait for hitTest or API calls.
      placeLocationPin(event.mapPoint.longitude, event.mapPoint.latitude)

      // Look up featureLayer on every click so we always get the current reference.
      // Pass it in include array only if found — null would cause hitTest to fail silently.
      const featureLayer = map.findLayerById(MAP_LAYER_IDS.content)
      const hitTestOptions = featureLayer ? { include: featureLayer } : {}

      view
        .hitTest(event, hitTestOptions)
        .then(response => {
          if (response && response.results && response.results.length > 0) {
            ghAnalytics.logFeatureInfoViewed(event.mapPoint, response.results.length)
            view.popup.alignment = 'top-right'

            const result = response.results[0]
            if (!result.graphic) { removePin(); return }
            const { attributes, geometry } = result.graphic
            if (attributes == null || attributes.uuid == null) { removePin(); return }
            if (attributes.uuid == 'search-point') { removePin(); return }

            const location = getLocation(attributes, geometry, event.mapPoint)

            if (attributes.uuid.includes('temp')) {
              removePin()
              view.popup.open({ title: 'Feature processing...', location, actions: [], content: '' })
              return
            }

            const { dispatch } = propsRef.current
            view.popup.close()
            dispatch({ type: 'layers/setFeatureAtLocation', payload: attributes })
            // Move spinner to the feature's precise location (may differ from click point)
            placeLocationPin(location.longitude, location.latitude)
            getSearchRequest([location.longitude, location.latitude, location.elevation || 0])
          } else {
            const { dispatch } = propsRef.current
            dispatch({ type: 'layers/setFeatureAtLocation', payload: null })
            getSearchRequest([event.mapPoint.longitude, event.mapPoint.latitude, event.mapPoint.z || 0])
          }
        })
        .catch(err => {
          console.error(err)
          removePin()
        })
    })

    return () => {
      clickListener.remove()
      popupEventListener.remove()
      dragListener.remove()
      viewpointWatch.remove()
      surface?.removeEventListener('pointerleave', resetCursor)
      spinnerEl.remove()
      removePin()
    }
  }, [props.view]) // re-register when the view switches (2D ↔ 3D)

  return null
}

ClickPopupHandler.propTypes = {
  activeLayers: PropTypes.arrayOf(PropTypes.object),
  activeOverlays: PropTypes.arrayOf(PropTypes.object),
  activeTool: PropTypes.any,
  dispatch: PropTypes.func,
  features: PropTypes.arrayOf(PropTypes.object),
  gotoLocationSidebar: PropTypes.any,
  hideCoords: PropTypes.any,
  isHistoricalView: PropTypes.bool,
  isTopographicBasemap: PropTypes.bool,
  historicalItem: PropTypes.object,
  isSidebarClosed: PropTypes.bool,
  isUpdatingLocation: PropTypes.bool,
  map: PropTypes.object,
  selectFeature: PropTypes.any,
  view: PropTypes.object,
  userToken: PropTypes.string,
  zoomLevel: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
}

export default connect((model, currentProps) => {
  const { activeTool, basemapMode, isUpdatingLocation, zoomLevel } = model.map
  const { currentUser } = model.user
  const { featuresList: features } = model.features
  const { hideCoords, sidebarView, showSidebar } = model.app
  const { layersByEvent, layersByState, overlays } = model.layers
  const isHistoricalView = sidebarView.view === SIDEBAR_VIEWS.HistoricalImagery
  const isTopographicBasemap = basemapMode === BASEMAPS.Topographic
  const isSidebarClosed = showSidebar === false
  const layersArray = [...layersByEvent, ...layersByState]
  let activeLayers = []
  let activeOverlays = overlays.filter(overlay => overlay.enabled)
  let historicalItem = null
  if (isHistoricalView && !isTopographicBasemap) {
    currentProps.view.popup.headingLevel = 4
    currentProps.view.popup.viewModel.includeDefaultActions = false
    historicalItem = model.historicalImagery.selectedItem
  } else {
    currentProps.view.popup.headingLevel = 3
    currentProps.view.popup.viewModel.includeDefaultActions = true
  }

  layersArray.forEach(layersModel => {
    let categories = layersModel.categories
    let layers = []

    categories.forEach(category => {
      if (hasLayers(category)) {
        let categoryLayers = category.layers
        layers.push(
          categoryLayers.map(layer => {
            return {
              ...layer,
              category: category.categoryName,
              subCategory: null,
              state: layersModel.stateName
            }
          })
        )
      }

      if (hasSubCategories(category)) {
        category.subCategories.forEach(subCat => {
          if (subCat.layers != null) {
            let subCategoryLayers = subCat.layers
            layers.push(
              subCategoryLayers.map(layer => {
                return {
                  ...layer,
                  category: category.categoryName,
                  subCategory: subCat.subCategoryName,
                  state: layersModel.stateName
                }
              })
            )
          }
        })
      }
    })

    layers = flatten(layers)
    activeLayers.push(layers)
  })
  activeLayers = flatten(activeLayers).filter(layer => {
    return layer.control.enabled == 1
  })

  return {
    activeLayers,
    activeOverlays,
    activeTool,
    features,
    hideCoords,
    historicalItem,
    isHistoricalView,
    isSidebarClosed,
    isTopographicBasemap,
    isUpdatingLocation,
    zoomLevel
  }
}, (dispatch, ownProps) => ({
  ...actions(dispatch, ownProps),
  dispatch,
}))(ClickPopupHandler)
